import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { toast } from 'react-toastify';

import qs from 'querystring';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { assign, isArray, isEmpty } from 'lodash';
import { format, parseISO } from 'date-fns';

import { Table } from '~/components';
import { Autocomplete, Input } from '~/components/Form';
import { t } from '~/helpers';
import api from '~/services/api';

import * as S from '../form/styles';

export default function Closure({
  data,
  sending,
  onForm,
  toggleDisabled,
  close,
}) {
  const formRef = useRef(null);

  const [histories, setHistories] = useState([]);
  const [loading, setLoading] = useState(false);

  const header = useMemo(
    () => [
      { id: 'post_desc', label: 'Posto' },
      {
        id: 'vemh_datahora',
        label: 'Data',
        format: (date) => format(parseISO(date), 'dd/MM/yyyy HH:mm'),
      },
      { id: 'bico_cod_bico', label: 'Bico' },
      { id: 'oper_cod_oper', label: 'Operador' },
    ],
    []
  );

  const handleSubmit = useCallback(
    async (values) => {
      try {
        toggleDisabled(true);

        const schema = Yup.object().shape({
          vems_nome_info_fecha: Yup.string()
            .typeError('Campo Obrigatório')
            .required('Campo Obrigatório'),
        });

        const modifyData = assign(
          {
            ...data,
            vems_status: data.vems_status_str,
            vems_datahora_abert: format(
              parseISO(data.vems_datahora_abert),
              'dd/MM/yyyy HH:mm:ss'
            ),
          },
          values
        );

        delete modifyData.empe_desc;
        delete modifyData.vems_msg_full;
        delete modifyData.vems_status;
        delete modifyData.usu_a_usua_nome;
        delete modifyData.usu_f_usua_nome;
        delete modifyData.post_desc;
        delete modifyData.rownum;
        delete modifyData.vems_status_str;

        await schema.validate(values, { abortEarly: false });
        await api.put(
          'msgtca/fechar',
          JSON.parse(JSON.stringify(modifyData).replace(/\:null/gi, ':""'))
        );

        toast.success('Veículo alterado com sucesso!');

        onForm();
        close();
      } catch (err) {
        const validationErrors = {};

        if (err instanceof Yup.ValidationError) {
          err.inner.forEach((error) => {
            validationErrors[error.path] = error.message.replace(
              `${error.path} `,
              ''
            );
          });

          formRef.current.setErrors(validationErrors);
        }
      } finally {
        toggleDisabled(false);
      }
    },
    [onForm, data]
  );

  const handleGetAPIHistory = useCallback(async (id) => {
    try {
      setLoading(true);

      const response = await api.get('msgtca/historico', {
        params: {
          wherefield: ['vems_cod_vems'],
          valuewherefield: [id],
          typewherefield: ['EQUALS'],
          pagesize: 99999,
        },
        paramsSerializer: (p) => qs.stringify(p),
      });

      setHistories(response.data.items);
    } catch (err) {
      if (isArray(err)) err.map((e) => toast.error(t(e)));
      else toast.error(t(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (sending) formRef.current.submitForm();
  }, [sending]);

  useEffect(() => {
    if (!isEmpty(data) && data.vems_cod_vems) {
      handleGetAPIHistory(data.vems_cod_vems);
    }
  }, [data]);

  return (
    <S.Wrapper closure ref={formRef} initialData={data} onSubmit={handleSubmit}>
      <Autocomplete
        disabled
        name="post_cod_posto_env"
        label="post_desc"
        url="usuarioposto"
        unique="post_cod_posto"
        title="Posto"
      />

      <Input name="vems_nome_solic" label="Solicitante Abertura" disabled />

      <Input name="vems_nome_info_fecha" label="Solicitante Fechamento" />

      <Input name="vems_msg01" label="MSG TCA Linha 1" disabled />

      <Input name="vems_msg02" label="MSG TCA Linha 2" disabled />

      <Input name="vems_msg03" label="MSG TCA Linha 3" disabled />

      <Input name="vems_msg04" label="MSG TCA Linha 4" disabled />

      <Input multiline name="vems_obs" label="Informação Geral" rows={4} />

      <Table
        title="Histórico de Confirmações"
        size="small"
        cells={header}
        data={histories}
        loading={loading}
      />
    </S.Wrapper>
  );
}

Closure.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  sending: PropTypes.bool.isRequired,
  onForm: PropTypes.func.isRequired,
  toggleDisabled: PropTypes.func.isRequired,
  close: PropTypes.func.isRequired,
};
